import { OUTCOME_TYPE } from 'pages/campaign/constant';

/**
 * Claw rules and shapes shared by the pure validator, the hook and the tables.
 *
 * Deliberately a dependency-free leaf so `campaignFormValidation`, a pure validator, does not have to
 * pull `clawColumns` -> `StyledImageInput` -> antd `Upload`, or `useClawSlots` -> React, into its
 * module graph. Keep this module free of component and hook imports.
 */

/**
 * Outcome types a claw box can bind to and issue.
 *
 * PRIZE_SET is ahead of the backend: `ClawPrizeConfigurationValidator::ALLOWED_OUTCOME_KINDS` (uat)
 * still rejects it, so a claw campaign carrying one 422s until the API PR lands. Safe because claw is
 * gated on CLAW_MACHINE, which stays off until then, and the menu entry re-checks PRIZE_SETS.
 */
export const CLAW_OUTCOME_TYPES: string[] = [
  OUTCOME_TYPE.REWARD,
  OUTCOME_TYPE.POINT,
  OUTCOME_TYPE.BADGE,
  OUTCOME_TYPE.PRIZE_SET,
  OUTCOME_TYPE.GAME_TRY,
  OUTCOME_TYPE.CUSTOM
];

/**
 * The single owner of "may this claw box bind that outcome type", list and flag rule together, so the
 * add-prize menu and the outcomes table cannot answer it differently.
 */
export const isClawOutcomeTypeAllowed = (
  outcomeType: string,
  {
    prizeSetsSupported,
    prizeMode
  }: { prizeSetsSupported: boolean; prizeMode?: 'fixed' | 'random' | null }
): boolean => {
  // A fixed-mode box pairs one icon to one prize, but a prize set only resolves to one of several
  // rewards when the play happens, so there is no single prize for an icon to stand for. Only random
  // mode, where every prize wears the same blind box, can bind one.
  if (outcomeType === OUTCOME_TYPE.PRIZE_SET) {
    return prizeSetsSupported && prizeMode === 'random';
  }
  return CLAW_OUTCOME_TYPES.includes(outcomeType);
};

export interface IClawSlotRow {
  _id?: string;
  slot?: number;
}

export const isAssignedSlot = (slot?: number | null): slot is number =>
  typeof slot === 'number' && Number.isInteger(slot) && slot > 0;
